import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery, 
    useTheme,
    TableCell as MuiTableCell,
    styled,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
    Grid,
    Chip
} from '@mui/material'
import useAccessCode from '../hooks/useAccessCode'
import { useNavigate } from 'react-router-dom'
import { IHistoryAccessCode, IHistoryAccessCodeFrecuente } from '../models/codes'
import { CustomModal } from '../components/Modal'
import { Done, DoneAll, Home, Edit, Save, Cancel, Delete } from '@mui/icons-material'

import { convertDate } from '../utils/date'
import { TableHeaderCell } from '../components/Table/TableHeaderCell'
import { ConfirmAndChild } from '../components/ConfirmWithChilds'

// Celda ultra compacta para móviles
const MobileTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: '4px 6px',
    fontSize: '0.7rem',
    lineHeight: '1.1',
    '&:first-of-type': {
        paddingLeft: '6px'
    },
    '&:last-of-type': {
        paddingRight: '6px'
    }
}))

export const HistorialAccesoFrecuentes = () => {
    const navigate = useNavigate()
    const { getHistoryFrecuente, deleteAccessCode, actualizarAccesoFrecuente } = useAccessCode()
    const [history, setHistory] = useState<IHistoryAccessCodeFrecuente[]>([])

    const [itemSelect, setItemSelect] = useState<IHistoryAccessCodeFrecuente>({} as IHistoryAccessCodeFrecuente)
    const [confirm, setConfirm] = useState<boolean>(false)

    const [openModal, setOpenModal] = useState(false)
    const [modalMessage, setModalMessage] = useState('')
    
    // Modal de detalle
    const [openDetailModal, setOpenDetailModal] = useState(false)
    const [selectedItem, setSelectedItem] = useState<IHistoryAccessCodeFrecuente | null>(null)
    const [isEditing, setIsEditing] = useState(false)
    const [editedItem, setEditedItem] = useState<Partial<IHistoryAccessCodeFrecuente>>({})

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const handleOpen = () => setOpenModal(true)
    const handleClose = () => {
        setOpenModal(false)
        navigate('/home')
    }

    // Funciones para el modal de detalle
    const handleOpenDetail = (item: IHistoryAccessCodeFrecuente) => {
        setSelectedItem(item)
        setEditedItem({})
        setIsEditing(false)
        setOpenDetailModal(true)
    }

    const handleCloseDetail = () => {
        setOpenDetailModal(false)
        setSelectedItem(null)
        setEditedItem({})
        setIsEditing(false)
    }

    const handleEditToggle = () => {
        if (!isEditing) {
            // Inicializar los valores editables cuando se entra en modo edición
            setEditedItem({
                desde: selectedItem?.desde,
                hasta: selectedItem?.hasta,
                hora_Inicial: selectedItem?.hora_Inicial,
                hora_Final: selectedItem?.hora_Final,
                lunes: selectedItem?.lunes,
                martes: selectedItem?.martes,
                miercoles: selectedItem?.miercoles,
                jueves: selectedItem?.jueves,
                viernes: selectedItem?.viernes,
                sabado: selectedItem?.sabado,
                domingo: selectedItem?.domingo,
                nota: selectedItem?.nota
            })
        }
        setIsEditing(!isEditing)
    }

    const handleFieldChange = (field: keyof IHistoryAccessCodeFrecuente, value: any) => {
        setEditedItem(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleSave = async () => {
        if (!selectedItem) return

        // Crear el objeto actualizado combinando el item original con los cambios
        const updatedItem: IHistoryAccessCodeFrecuente = {
            ...selectedItem,
            ...editedItem
        }



        try {
            const result = await actualizarAccesoFrecuente(updatedItem)
            
            if (result) {
                // Si llegó aquí, significa que el status fue 200 (éxito)
                // Actualizar el item en la lista local
                setHistory(prevHistory => 
                    prevHistory.map(item => 
                        item.codigo_Acceso === selectedItem.codigo_Acceso ? updatedItem : item
                    )
                )
                
                // Actualizar el item seleccionado
                setSelectedItem(updatedItem)
                
                // Salir del modo edición
                setIsEditing(false)
                setEditedItem({})
                
                // Mostrar mensaje de éxito
                setModalMessage('Acceso actualizado correctamente')
                handleOpen()
            } else {
                // Si result es false, significa que hubo error (status !== 200)
                setModalMessage('Error al actualizar el acceso')
                handleOpen()
            }
        } catch (error) {
            console.error('Error al guardar:', error)
            setModalMessage('Error de sistema al actualizar el acceso')
            handleOpen()
        }
    }

    useEffect(() => {
        (async () => {
            let history = await getHistoryFrecuente()
            if (history) {
                setHistory(history)
            } else {
                setModalMessage('Error al obtener el historial')
                handleOpen()
            }
        })()
    }, [])

    const AnularReserva = (codigo) => async () => {
        let code = await deleteAccessCode(codigo)
        if (code && code.success) {
            setModalMessage('Acceso anulado')
            setHistory(history.filter(item => item.codigo_Acceso.toString() !== codigo))
        } else if (code) {
            setModalMessage(code.message)
        } else {
            setModalMessage('Error de sistema al anular el código de acceso')
        }
        handleOpen()
    }

    const getStatusIcon = (item: IHistoryAccessCodeFrecuente) => {
        const hoy = new Date()
        
        // Las fechas vienen en formato "MM/dd/yyyy HH:mm:ss"
        const fechaDesde = new Date(item.desde)
        const fechaHasta = new Date(item.hasta)
        
        // Si las fechas son inválidas, mostrar estado de error
        if (isNaN(fechaDesde.getTime()) || isNaN(fechaHasta.getTime())) {
            return {
                icon: <Done fontSize="small" sx={{ color: 'gray' }} />,
                tooltip: 'Fechas inválidas'
            }
        }
        
        // Verificar si el acceso está dentro del rango de fechas válidas
        if (fechaDesde <= hoy && fechaHasta > hoy && item.estado === 1) {
            return {
                icon: <DoneAll fontSize="small" sx={{ color: 'green' }} />,
                tooltip: 'Acceso activo'
            }
        }
        
        // Si la fecha ya pasó
        if (fechaHasta <= hoy) {
            return {
                icon: <Done fontSize="small" color="disabled" />,
                tooltip: 'Acceso vencido'
            }
        }
        
        // Si el acceso está anulado
        if (item.estado !== 1) {
            return {
                icon: <Done fontSize="small" sx={{ color: 'red' }} />,
                tooltip: 'Acceso anulado'
            }
        }
        
        // Si aún no ha comenzado
        return {
            icon: <Done fontSize="small" sx={{ color: 'orange' }} />,
            tooltip: 'Acceso programado'
        }
    }
    const isRowClickable = (item: IHistoryAccessCodeFrecuente) => {
        const hoy = new Date()
        
        // Las fechas vienen en formato "MM/dd/yyyy HH:mm:ss"
        const fechaDesde = new Date(item.desde)
        const fechaHasta = new Date(item.hasta)
        
        // Si las fechas son inválidas, no es clickeable
        if (isNaN(fechaDesde.getTime()) || isNaN(fechaHasta.getTime())) {
            return false
        }
        
        // Un acceso frecuente es clickeable si está activo (dentro del rango de fechas y no anulado)
        return fechaDesde <= hoy && fechaHasta > hoy && item.estado === 1
    }

    return ( 
        <>
            <ConfirmAndChild
                message={
                    <center>
                        
                        <Typography variant="body1">¿Anular código de acceso?</Typography>
                        <Typography variant="h4">{itemSelect.codigo_Acceso}</Typography>

                    </center>
                }
                okText='Anular'
                okAction={AnularReserva(itemSelect.codigo_Acceso)}
                show={confirm}
                setShow={setConfirm}
            />

            <Box textAlign="center" display="flex" alignItems="center" flexDirection='column' p={1}>
                <Typography variant={isSmallScreen ? "h6" : "h5"} gutterBottom>
                    Mis Accesos Frecuentes
                </Typography>

                <Box width="100%" overflow="auto">
                    <Table
                        size="small"
                        sx={{
                            minWidth: isSmallScreen ? '100%' : 800,
                            '& .MuiTableCell-root': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            },
                            '& .MuiTableHead-root': {
                                '& .MuiTableCell-root': {
                                    fontSize: isSmallScreen ? '0.75rem' : '0.8125rem',
                                    fontWeight: 'bold',
                                    padding: isSmallScreen ? '8px 6px' : '12px 8px'
                                }
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '10%' }}>Código</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '25%' : '25%' }}>Nombre</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '15%' }}>Vigencia</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '20%' }}>Hora Inicio</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '20%' }}>Hora Fin</TableHeaderCell>
                                <TableHeaderCell sx={{ width: isSmallScreen ? '15%' : '20%' }}>Usos</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.map((item, index) => {
                                const TableCellComponent = isSmallScreen ? MobileTableCell : MuiTableCell
                                const status = getStatusIcon(item)

                                // Validar y formatear fechas de manera segura
                                // Las fechas vienen en formato "MM/dd/yyyy HH:mm:ss"
                                const fechaDesdeObj = new Date(item.desde);
                                const fechaHastaObj = new Date(item.hasta);
                                
                                const fechaDesde = fechaDesdeObj && !isNaN(fechaDesdeObj.getTime()) 
                                    ? fechaDesdeObj.toISOString().split("T")[0] 
                                    : 'Fecha inválida'
                                const fechaHasta = fechaHastaObj && !isNaN(fechaHastaObj.getTime()) 
                                    ? fechaHastaObj.toISOString().split("T")[0] 
                                    : 'Fecha inválida'
                                
                                // Las horas vienen como enteros simples (ej: 8, 17)
                                const horaInicio = item.hora_Inicial 
                                    ? `${String(item.hora_Inicial).padStart(2, '0')}:00`
                                    : '--:--'
                                const horaFin = item.hora_Final 
                                    ? `${String(item.hora_Final).padStart(2, '0')}:00`
                                    : '--:--'
                                const clickable = isRowClickable(item)

                                return (
                                    <TableRow
                                        key={index}
                                        hover={clickable}
                                        
                                        onClick={() => handleOpenDetail(item)}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor:theme.palette.action.hover 
                                            }
                                        }}
                                    >
                                        <TableCellComponent>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1, // Espacio entre icono y texto
                                                    maxWidth: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <Tooltip title={status.tooltip}>
                                                    {status.icon}
                                                </Tooltip>
                                                <Tooltip title={item.codigo_Acceso.toString()}>
                                                    <span style={{
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {item.codigo_Acceso}
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={item.nombre}>
                                                <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                    {item.nombre}
                                                </Box>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={`Desde: ${fechaDesde} | Hasta: ${fechaHasta}`}>
                                                <span>
                                                    {isSmallScreen 
                                                        ? (fechaDesde !== 'Fecha inválida' ? fechaDesde.split('-').reverse().join('/') : 'N/A')
                                                        : `${fechaDesde} - ${fechaHasta}`
                                                    }
                                                </span>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={`Hora de inicio: ${horaInicio}`}>
                                                <span>{horaInicio}</span>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <Tooltip title={`Hora de fin: ${horaFin}`}>
                                                <span>{horaFin}</span>
                                            </Tooltip>
                                        </TableCellComponent>
                                        <TableCellComponent>
                                            <span>{item.usos}</span>
                                        </TableCellComponent>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>

                <CustomModal
                    openState={openModal}
                    onCloseHandler={handleClose}
                >
                    <Typography variant={isSmallScreen ? "body1" : "h6"}>{modalMessage}</Typography>
                    <Button
                        color='secondary'
                        variant="outlined"
                        endIcon={<Home />}
                        sx={{
                            mt: 2,
                            width: isSmallScreen ? '90%' : '50%',
                            py: 1
                        }}
                        onClick={handleClose}
                        size={isSmallScreen ? "small" : "medium"}
                    >
                        Inicio
                    </Button>
                </CustomModal>

                {/* Modal de Detalle */}
                <Dialog 
                    open={openDetailModal} 
                    onClose={handleCloseDetail}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isSmallScreen}
                >
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">
                                Detalle del Acceso Frecuente
                            </Typography>
                            {selectedItem && (
                                <Chip 
                                    label={getStatusIcon(selectedItem).tooltip}
                                    color={
                                        getStatusIcon(selectedItem).tooltip === 'Acceso activo' ? 'success' :
                                        getStatusIcon(selectedItem).tooltip === 'Acceso vencido' ? 'default' :
                                        getStatusIcon(selectedItem).tooltip === 'Acceso anulado' ? 'error' :
                                        'warning'
                                    }
                                    icon={getStatusIcon(selectedItem).icon}
                                />
                            )}
                        </Box>
                    </DialogTitle>
                    
                    <DialogContent>
                        {selectedItem && (
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Código de Acceso"
                                        value={selectedItem.codigo_Acceso}
                                        disabled
                                        variant="outlined"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        value={selectedItem.nombre || ''}
                                        disabled
                                        variant="outlined"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Desde"
                                        value={
                                            isEditing && editedItem.desde ? 
                                            new Date(editedItem.desde).toISOString().split('T')[0] :
                                            selectedItem.desde ? new Date(selectedItem.desde).toISOString().split('T')[0] : ''
                                        }
                                        disabled={!isEditing}
                                        type="date"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => handleFieldChange('desde', e.target.value)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha Hasta"
                                        value={
                                            isEditing && editedItem.hasta ? 
                                            new Date(editedItem.hasta).toISOString().split('T')[0] :
                                            selectedItem.hasta ? new Date(selectedItem.hasta).toISOString().split('T')[0] : ''
                                        }
                                        disabled={!isEditing}
                                        type="date"
                                        variant="outlined"
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(e) => handleFieldChange('hasta', e.target.value)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hora Inicial"
                                        value={
                                            isEditing && editedItem.hora_Inicial !== undefined ? 
                                            editedItem.hora_Inicial : 
                                            selectedItem.hora_Inicial || ''
                                        }
                                        disabled={!isEditing}
                                        type="number"
                                        variant="outlined"
                                        inputProps={{ min: 0, max: 23 }}
                                        onChange={(e) => handleFieldChange('hora_Inicial', parseInt(e.target.value) || 0)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Hora Final"
                                        value={
                                            isEditing && editedItem.hora_Final !== undefined ? 
                                            editedItem.hora_Final : 
                                            selectedItem.hora_Final || ''
                                        }
                                        disabled={!isEditing}
                                        type="number"
                                        variant="outlined"
                                        inputProps={{ min: 0, max: 23 }}
                                        onChange={(e) => handleFieldChange('hora_Final', parseInt(e.target.value) || 0)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Días de la semana:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.lunes !== undefined ? 
                                                        Boolean(editedItem.lunes) : 
                                                        Boolean(selectedItem.lunes)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('lunes', e.target.checked)}
                                                />
                                            }
                                            label="Lunes"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.martes !== undefined ? 
                                                        Boolean(editedItem.martes) : 
                                                        Boolean(selectedItem.martes)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('martes', e.target.checked)}
                                                />
                                            }
                                            label="Martes"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.miercoles !== undefined ? 
                                                        Boolean(editedItem.miercoles) : 
                                                        Boolean(selectedItem.miercoles)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('miercoles', e.target.checked)}
                                                />
                                            }
                                            label="Miércoles"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.jueves !== undefined ? 
                                                        Boolean(editedItem.jueves) : 
                                                        Boolean(selectedItem.jueves)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('jueves', e.target.checked)}
                                                />
                                            }
                                            label="Jueves"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.viernes !== undefined ? 
                                                        Boolean(editedItem.viernes) : 
                                                        Boolean(selectedItem.viernes)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('viernes', e.target.checked)}
                                                />
                                            }
                                            label="Viernes"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.sabado !== undefined ? 
                                                        Boolean(editedItem.sabado) : 
                                                        Boolean(selectedItem.sabado)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('sabado', e.target.checked)}
                                                />
                                            }
                                            label="Sábado"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={
                                                        isEditing && editedItem.domingo !== undefined ? 
                                                        Boolean(editedItem.domingo) : 
                                                        Boolean(selectedItem.domingo)
                                                    } 
                                                    disabled={!isEditing}
                                                    onChange={(e) => handleFieldChange('domingo', e.target.checked)}
                                                />
                                            }
                                            label="Domingo"
                                        />
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nota"
                                        value={
                                            isEditing && editedItem.nota !== undefined ? 
                                            editedItem.nota : 
                                            selectedItem.nota || ''
                                        }
                                        disabled={!isEditing}
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        onChange={(e) => handleFieldChange('nota', e.target.value)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Imagen DPI:
                                        </Typography>
                                        {selectedItem.dpi && selectedItem.dpi !== "NA" ? (
                                            <Box
                                                component="img"
                                                src={selectedItem.dpi}
                                                alt="DPI"
                                                sx={{
                                                    maxWidth: '100%',
                                                    maxHeight: 200,
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 1
                                                }}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                    if (nextElement) {
                                                        nextElement.style.display = 'block';
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Sin imagen
                                            </Typography>
                                        )}
                                        
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Foto:
                                        </Typography>
                                        {selectedItem.foto && selectedItem.foto !== "NA" ? (
                                            <Box
                                                component="img"
                                                src={selectedItem.foto}
                                                alt="Foto"
                                                sx={{
                                                    maxWidth: '100%',
                                                    maxHeight: 200,
                                                    objectFit: 'contain',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 1
                                                }}
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                    if (nextElement) {
                                                        nextElement.style.display = 'block';
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Sin imagen
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Correo"
                                        value={selectedItem.correo || ''}
                                        disabled
                                        variant="outlined"
                                        type="email"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Usos"
                                        value={selectedItem.usos || 0}
                                        disabled
                                        variant="outlined"
                                        type="number"
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </DialogContent>
                    
                    <DialogActions>
                        <Button onClick={handleCloseDetail} color="inherit">
                            Cerrar
                        </Button>
                        
                        {selectedItem && isRowClickable(selectedItem) && (
                            <>
                                {!isEditing ? (
                                    <Button 
                                        onClick={handleEditToggle} 
                                        color="primary" 
                                        startIcon={<Edit />}
                                    >
                                        Editar
                                    </Button>
                                ) : (
                                    <>
                                        <Button 
                                            onClick={handleEditToggle} 
                                            color="inherit" 
                                            startIcon={<Cancel />}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button 
                                            onClick={handleSave} 
                                            color="primary" 
                                            startIcon={<Save />}
                                        >
                                            Guardar
                                        </Button>
                                    </>
                                )}
                                
                                <Button 
                                    onClick={() => {
                                        setItemSelect(selectedItem)
                                        setConfirm(true)
                                        handleCloseDetail()
                                    }} 
                                    color="error" 
                                    startIcon={<Delete />}
                                >
                                    Anular
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    )
}